import type { CheckoutCartAddress } from '~~/layers/base/types/checkout/setShippingAddressOnGuestCart'

/** Profile/contact `address` row fields used for UI previews. */
export type DbAddressDisplayFields = {
    raw_address_str?: string | null
    street_address_1?: string | null
    street_address_2?: string | null
    city?: string | null
    state?: string | null
    zip_code?: string | null
}

/** Structured addressee columns on an address row (envelope name line). */
export type AddressAddresseeFields = {
    prefix?: string | null
    first_name?: string | null
    last_name?: string | null
    suffix?: string | null
}

/** Profile first/last when the address row has no addressee columns (return address only). */
export type AddressAddresseeFallback = {
    firstName?: string | null
    lastName?: string | null
}

/**
 * Address shapes accepted by format helpers: DB/profile rows or ACCS checkout cart addresses
 * (`shoppingSession.addresses`). Names / telephone are never included in display output.
 */
export type FormatAddressInput = DbAddressDisplayFields | CheckoutCartAddress

type NormalizedAddressParts = {
    raw: string | null
    street1: string | null
    street2: string | null
    city: string | null
    state: string | null
    zip: string | null
}

function isCheckoutCartAddress(addr: FormatAddressInput): addr is CheckoutCartAddress {
    return 'street' in addr && Array.isArray(addr.street)
}

/**
 * Maps either a DB address row or a `CheckoutCartAddress` onto shared display parts.
 * Omits firstname/lastname/telephone — callers that need a name own that separately.
 */
function normalizeAddressParts(addr: FormatAddressInput): NormalizedAddressParts {
    if (isCheckoutCartAddress(addr)) {
        return {
            raw: null,
            street1: addr.street[0]?.trim() || null,
            street2: addr.street[1]?.trim() || null,
            city: addr.city?.trim() || null,
            state: addr.region?.trim() || null,
            zip: addr.postcode?.trim() || null,
        }
    }
    return {
        raw: addr.raw_address_str?.trim() || null,
        street1: addr.street_address_1?.trim() || null,
        street2: addr.street_address_2?.trim() || null,
        city: addr.city?.trim() || null,
        state: addr.state?.trim() || null,
        zip: addr.zip_code?.trim() || null,
    }
}

/**
 * Format an address to a single display line.
 * Accepts DB/profile rows (`raw_address_str` / street_address_*) or checkout cart addresses
 * (`street[]` / `region` / `postcode` from `shoppingSession.addresses`).
 * Uses raw when set; otherwise joins street lines + city/state/zip. Country and person name omitted.
 */
export function formatAddressLine(addr: FormatAddressInput): string {
    const parts = normalizeAddressParts(addr)
    if (parts.raw) {
        return parts.raw.replace(/\s+/g, ' ').trim()
    }
    const cityStateZip = [parts.city, parts.state, parts.zip].filter(Boolean).join(', ')
    return [parts.street1, parts.street2, cityStateZip || null].filter(Boolean).join(', ')
}

/**
 * Format an address to an array of lines.
 * Same input shapes as {@link formatAddressLine}; country and person name omitted.
 */
export function formatAddressLines(addr: FormatAddressInput): string[] {
    const parts = normalizeAddressParts(addr)
    if (parts.raw) {
        return parts.raw.split('\n')
    }
    const cityStateZip = [parts.city, parts.state, parts.zip].filter(Boolean).join(', ')
    return [parts.street1, parts.street2, cityStateZip || null].filter(Boolean) as string[]
}

/**
 * Envelope / picker display: at most two lines.
 * Street (+ apt / line 2) on the first line; city/state/zip alone on the second.
 * MVP `.hm-contact-card` / `.hm-picker-address` (not one line per field).
 */
export function formatAddressTwoLines(addr: FormatAddressInput): string[] {
    return collapseToAddressTwoLines(formatAddressLines(addr))
}

/** Collapse any line list to street… + region (last line). */
export function collapseToAddressTwoLines(lines: string[]): string[] {
    const all = lines.map((l) => l.trim()).filter(Boolean)
    if (all.length <= 1) return all
    return [all.slice(0, -1).join(', '), all[all.length - 1]!]
}

export function addressHasStructuredAddressee(fields: AddressAddresseeFields): boolean {
    return Boolean(
        fields.prefix?.trim() ||
            fields.first_name?.trim() ||
            fields.last_name?.trim() ||
            fields.suffix?.trim()
    )
}

export type AddressCardAddresseeContext = {
    /** Profile first/last when the address row has no addressee columns (return address). */
    addresseeFallback?: AddressAddresseeFallback | null
    /** Contact display name when the address row has no addressee columns. */
    contactName?: string | null
}

function normalizeAddressCardAddresseeContext(
    context?: AddressCardAddresseeContext | AddressAddresseeFallback | null
): AddressCardAddresseeContext | null {
    if (!context) return null
    if ('addresseeFallback' in context || 'contactName' in context) {
        return context
    }
    return { addresseeFallback: context }
}

/**
 * Resolves envelope addressee fields for summary cards and picker rows.
 * Address addressee columns win; else profile first/last; else contact display name.
 */
export function resolveAddressCardAddressee(
    address: AddressAddresseeFields,
    context?: AddressCardAddresseeContext | AddressAddresseeFallback | null
): AddressAddresseeFields {
    const normalized = normalizeAddressCardAddresseeContext(context)

    if (addressHasStructuredAddressee(address)) {
        return {
            prefix: address.prefix,
            first_name: address.first_name,
            last_name: address.last_name,
            suffix: address.suffix,
        }
    }

    const fbFirst = normalized?.addresseeFallback?.firstName?.trim() ?? ''
    const fbLast = normalized?.addresseeFallback?.lastName?.trim() ?? ''
    if (fbFirst || fbLast) {
        return {
            prefix: address.prefix,
            first_name: fbFirst || null,
            last_name: fbLast || null,
            suffix: address.suffix,
        }
    }

    const contactLine = normalized?.contactName?.trim()
    if (contactLine) {
        return {
            prefix: null,
            first_name: contactLine,
            last_name: null,
            suffix: null,
        }
    }

    return {
        prefix: address.prefix ?? null,
        first_name: '',
        last_name: '',
        suffix: address.suffix ?? null,
    }
}

/** Single-line addressee for picker action rows, cards, and aria labels. */
export function formatAddresseeLine(fields: AddressAddresseeFields): string {
    const parts = [fields.prefix, fields.first_name, fields.last_name]
        .filter(Boolean)
        .join(' ')
        .trim()
    return fields.suffix ? `${parts}, ${fields.suffix}`.trim() : parts
}
